/**
 * Deletes and two people editing a theme (screenshots/90; §2.15):
 * - DeleteThemeDialog: names every overview that uses the theme and how many charts, says they'll switch to the team
 *   default (Civic Light when this theme is the default) and that single-chart colors stay. The server re-points them
 *   (worker/src/dashboard-themes.js) and broadcasts `theme.deleted`.
 * - ThemeConflictPanel: someone saved while you were editing. What only they changed is already merged (listed); each slot
 *   both of you changed gets a Yours / Theirs choice (Yours by default), then the palette checks run again before
 *   "Save merged theme" (themeStore.resolve).
 */
import { useEffect, useRef, useState } from "react";
import { CIcon } from "./icons";
import { resolveThemeConflicts, validateTheme, type ThemeMerge } from "./themes";

type Usage = { overview: string; charts: number }[];

export function DeleteThemeDialog({ name, usage, fallback, onCancel, onDelete }: { name: string; usage: Usage; fallback: string; onCancel: () => void; onDelete: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.querySelector<HTMLButtonElement>(".btn--secondary")?.focus(); }, []);
  const charts = usage.reduce((a, u) => a + u.charts, 0);
  const n = (k: number, w: string) => `${k} ${w}${k === 1 ? "" : "s"}`;
  return (
    <div className="cs-backdrop te-confirm__backdrop" onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}>
      <div ref={ref} className="te-confirm" role="alertdialog" aria-modal="true" aria-labelledby="te-del-title" aria-describedby="te-del-body">
        <h2 id="te-del-title">Delete “{name}”?</h2>
        <p id="te-del-body">{charts ? `${n(charts, "chart")} on ${n(usage.length, "overview")} use this theme. They’ll switch to ${fallback}, your team default. ` : ""}Colors set on a single chart stay as they are.</p>
        {usage.length > 0 && (
          <ul className="te-confirm__uses">{usage.map((u) => <li key={u.overview}><span>{u.overview}</span><span>{n(u.charts, "chart")}</span></li>)}</ul>
        )}
        <div className="te-confirm__actions">
          <button type="button" className="btn btn--secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn--danger" onClick={onDelete}><CIcon name="trash-2" />Delete theme</button>
        </div>
      </div>
    </div>
  );
}

const ago = (iso?: string) => {
  const m = iso ? Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000)) : 0;
  return m < 1 ? "just now" : `${m} min ago`;
};
const listText = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);

export function ThemeConflictPanel({ merge, by, theirsAt, onResolve }: { merge: ThemeMerge; by: string; theirsAt?: string; onResolve: (keep: Record<string, "mine" | "theirs">) => void }) {
  const [keep, setKeep] = useState<Record<string, "mine" | "theirs">>({});
  const [checked, setChecked] = useState<string[] | null>(null);
  const swatch = (v: unknown) => (typeof v === "string" && /^#/.test(v) ? <i className="te-conflict__sw" style={{ background: v }} /> : null);
  const show = (v: unknown) => (Array.isArray(v) ? v.join(" ") : String(v));
  return (
    <div className="te-conflict" role="region" aria-label="Edit conflict">
      <p className="ov-banner ov-banner--warning"><CIcon name="git-merge" size={15} />{by} saved this theme {ago(theirsAt)} while you were editing.</p>
      {merge.fromTheirs.length > 0 && <p className="te-conflict__merged"><CIcon name="circle-check" size={14} />Merged: {by}’s {listText(merge.fromTheirs.map((s) => s.toLowerCase()))}.</p>}
      {merge.conflicts.map((c) => (
        <fieldset key={c.slot} className="te-conflict__slot">
          <legend>{c.label} · both changed</legend>
          {(["mine", "theirs"] as const).map((side) => (
            <label key={side} className={`te-conflict__opt${(keep[c.slot] ?? "mine") === side ? " is-on" : ""}`}>
              <input type="radio" name={c.slot} checked={(keep[c.slot] ?? "mine") === side} onChange={() => { setKeep({ ...keep, [c.slot]: side }); setChecked(null); }} />
              {swatch(side === "mine" ? c.mine : c.theirs)}<code>{show(side === "mine" ? c.mine : c.theirs)}</code><span>{side === "mine" ? "Yours" : `${by}’s`}</span>
            </label>
          ))}
        </fieldset>
      ))}
      {checked && <ul className="cs-checks">{checked.length ? checked.map((p) => <li key={p} className="is-fail"><CIcon name="circle-x" size={13} />{p}</li>)
        : <li className="is-pass"><CIcon name="circle-check" size={13} />Passes</li>}</ul>}
      <div className="te-conflict__actions">
        <button type="button" className="btn btn--secondary" onClick={() => setChecked(validateTheme(resolveThemeConflicts(merge, keep)).issues)}>Review checks</button>
        <button type="button" className="btn btn--primary" onClick={() => onResolve(keep)}><CIcon name="check" />Save merged theme</button>
      </div>
    </div>
  );
}
